/**
 * QuickCapture — row-based bulk task entry.
 *
 * The widget is rows-by-default: each task is its own editable row with
 * Title | Due | Priority | Energy | Time cells. NLP parsing in the title
 * cell triggers on Enter / Tab-out and auto-fills the other cells (only
 * those still at their default value, so manual edits are preserved).
 * "#Header" lines (typed or pasted) become section dividers.
 *
 * Bulk paste is supported via a "Paste many lines" button that explodes
 * multi-line text into rows, or by directly pasting multi-line content
 * into any title cell.
 *
 * Submit creates everything as-is (auto-accepting fuzzy suggestions and
 * any unconfirmed "new project" headers). Verify opens the drag-and-drop
 * review dialog for explicit per-section control.
 */

import { useCallback, useEffect, useMemo, useState, useRef } from 'react'
import { Plus, Loader2, FolderPlus, Lightbulb, X, Check, SlidersHorizontal, ListOrdered, ChevronRight, ChevronDown } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import { ProjectPicker } from '@/components/ProjectPicker'
import type { Project, Task, CreateTaskInput } from '@/types'
import {
  rowsToSections,
  countNewProjectsOnSubmit,
  makeEmptyTaskRow,
  textToRows,
  type BulkSection,
  type ProjectResolution,
  type RowItem,
} from '@/utils/bulkTaskParser'
import { BulkRowCapture } from './BulkRowCapture'
import { BulkAddReview } from './BulkAddReview'
import { withCaptureDefaults, deadlineFromKey, dateKey } from '@trackmind/core'
import { cn } from '@/lib/utils'

interface QuickCaptureProps {
  projects: Project[]
  /** Returns the created task where the host can — needed to chain them. */
  onCapture: (data: CreateTaskInput) => Promise<Task | void>
  onCreateProject?: (name: string) => Promise<Project>
  defaultProjectId?: string
  // When the surrounding context already pins the project (e.g. ProjectView),
  // hide the project picker. The bulk parser still honours "#Header" lines
  // for redirecting individual tasks to other projects.
  hideProjectPicker?: boolean
  // Mount the widget pre-expanded (rows already visible) instead of as the
  // collapsed "Quick capture..." button. Useful when QuickCapture is the
  // sole content of a dialog / overlay where the trigger lives elsewhere.
  initiallyExpanded?: boolean
  /**
   * Told when the stage-2 review dialog opens and closes.
   *
   * A host that is itself a dialog needs this: it must step aside rather than
   * close (closing would unmount this component and take the review with it),
   * or the user ends up two sheets deep with the wrong one behind.
   */
  onReviewOpenChange?: (open: boolean) => void
  /**
   * Chain the tasks just created, in the order they were typed. Given by a
   * host that can write dependencies; without it the Sequence toggle is
   * hidden, since offering a switch that does nothing is worse than not
   * offering it.
   */
  onSequence?: (created: Task[]) => Promise<unknown>
  /**
   * Called once a submit has actually created everything.
   *
   * A host that opened this in a dialog has nothing left to show at that
   * point — leaving the dialog up meant the board reappeared behind an empty
   * capture form you then had to dismiss by hand.
   */
  onDone?: () => void
}

export function QuickCapture({
  projects,
  onCapture,
  onCreateProject,
  defaultProjectId,
  hideProjectPicker,
  initiallyExpanded,
  onReviewOpenChange,
  onDone,
  onSequence,
}: QuickCaptureProps) {
  const [rows, setRows] = useState<RowItem[]>(() => [makeEmptyTaskRow()])
  const [projectId, setProjectId] = useState(defaultProjectId || '')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [isExpanded, setIsExpanded] = useState(!!initiallyExpanded)
  const [reviewOpen, setReviewOpenState] = useState(false)
  const setReviewOpen = useCallback((open: boolean) => {
    setReviewOpenState(open)
    onReviewOpenChange?.(open)
  }, [onReviewOpenChange])
  // One-tap phone capture (Keep/Todoist style): the collapsed bar is a real
  // input — tap → keyboard → type → Enter. Same parse/submit pipeline as the
  // full editor, so NLP and "#Project task" redirects still work.
  // Off by default: most captures are a pile of unrelated tasks, and a
  // dependency you didn't ask for stops work being scheduled.
  const [sequenceMode, setSequenceMode] = useState(false)
  const [optionsOpen, setOptionsOpen] = useState(false)
  const [quickText, setQuickText] = useState('')
  const [justAdded, setJustAdded] = useState(false)
  const quickInputRef = useRef<HTMLInputElement>(null)

  // Set default project if not set
  useEffect(() => {
    if (!projectId && projects.length > 0) {
      const active = projects.filter(p => p.status === 'active')
      if (active.length > 0) setProjectId(active[0].id)
    }
  }, [projects, projectId])

  // ─── Derived state ──────────────────────────────────────────────────────

  const sections: BulkSection[] = useMemo(() => rowsToSections(rows), [rows])

  const totalTasks = useMemo(
    () => sections.reduce((n, s) => n + s.tasks.length, 0),
    [sections]
  )

  const newProjectCount = useMemo(() => countNewProjectsOnSubmit(sections), [sections])

  const fuzzyCount = useMemo(
    () => sections.filter(s => s.resolution.kind === 'fuzzy-suggestion').length,
    [sections]
  )

  const hasContent = totalTasks > 0 || rows.some(r => r.kind === 'header')

  // ─── Submit ─────────────────────────────────────────────────────────────

  // Auto-accept fuzzy suggestions and confirm NEW headers (direct-submit path).
  const autoAccept = (input: BulkSection[]): BulkSection[] =>
    input.map(s => {
      const r = s.resolution
      if (r.kind === 'fuzzy-suggestion') {
        return {
          ...s,
          resolution: {
            kind: 'user-mapped-existing',
            projectId: r.suggestedProjectId,
            rawName: r.rawName,
          } as ProjectResolution,
        }
      }
      if (r.kind === 'new') {
        return {
          ...s,
          resolution: { kind: 'user-confirmed-new', rawName: r.rawName } as ProjectResolution,
        }
      }
      return s
    })

  const commitSections = async (input: BulkSection[]) => {
    // First, resolve every section to a real project id.
    const idForSection = new Map<string, string>()
    for (const s of input) {
      const r = s.resolution
      if (r.kind === 'default') {
        idForSection.set(s.id, projectId)
      } else if (r.kind === 'existing-exact' || r.kind === 'user-mapped-existing') {
        idForSection.set(s.id, r.projectId)
      } else if (r.kind === 'user-confirmed-new') {
        if (!onCreateProject) throw new Error('Project creation not available here.')
        const np = await onCreateProject(r.rawName)
        idForSection.set(s.id, np.id)
      } else {
        // 'new' or 'fuzzy-suggestion' should have been auto-accepted upstream.
        throw new Error(`Unresolved section: ${s.rawHeader ?? '(default)'}`)
      }
    }

    // Create every task sequentially so debounced sync only fires once
    // per chunk and IndexedDB writes stay ordered. The created tasks are
    // collected because chaining them needs ids that don't exist yet.
    const created: Task[] = []
    for (const s of input) {
      const pid = idForSection.get(s.id)
      if (!pid) continue
      for (const t of s.tasks) {
        const p = t.parsed
        // Defaults live in core so every capture door shares them — the MCP
        // connector's "add a task" fills the same blanks the same way. See
        // services/capture-defaults.ts; `needs_review: false` is in there
        // because quick capture always knows its project.
        const made = await onCapture({
          project_id: pid,
          title: p.title || t.rawLine,
          ...withCaptureDefaults({
            priority: p.priority,
            energy_level: p.energyLevel,
            estimated_minutes: p.estimatedMinutes,
          }),
          has_deadline: !!p.deadline,
          // The parsed day, stored in the one deadline format. `toISOString()`
          // stored a UTC instant — an LA evening's "tomorrow" became the day after.
          deadline: p.deadline ? deadlineFromKey(dateKey(p.deadline)) : undefined,
        })
        if (made) created.push(made)
      }
    }

    // "Do these in this order": one toggle instead of nine trips through the
    // task dialog. Chained in the order the lines were typed, across section
    // headers too — a sequence that redirects a step to another project is
    // still one sequence.
    if (sequenceMode && onSequence && created.length > 1) {
      await onSequence(created)
    }
    return created
  }

  const handleDirectSubmit = async () => {
    if (!projectId || totalTasks === 0) return
    setIsSubmitting(true)
    try {
      await commitSections(autoAccept(sections))
      // Reset to one fresh empty row
      setRows([makeEmptyTaskRow()])
      onDone?.()
    } catch (err) {
      console.error('Bulk submit failed:', err)
      alert(err instanceof Error ? err.message : 'Bulk add failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleQuickAdd = async () => {
    const line = quickText.trim()
    if (!line || isSubmitting) return
    const parsedRows = textToRows(line, projects)
    const secs = rowsToSections(parsedRows)
    const count = secs.reduce((n, sec) => n + sec.tasks.length, 0)
    if (count === 0 || !projectId) {
      // Only a #header (or no default project yet) — open the full editor
      expandWithSeed()
      return
    }
    setIsSubmitting(true)
    try {
      await commitSections(autoAccept(secs))
      setQuickText('')
      setJustAdded(true)
      setTimeout(() => setJustAdded(false), 1200)
      // Keep the keyboard up so several tasks can be dumped in a row
      quickInputRef.current?.focus()
    } catch (err) {
      console.error('Quick add failed:', err)
      alert(err instanceof Error ? err.message : 'Quick add failed')
    } finally {
      setIsSubmitting(false)
    }
  }

  const expandWithSeed = () => {
    const line = quickText.trim()
    if (line) {
      setRows([...textToRows(line, projects), makeEmptyTaskRow()])
      setQuickText('')
    }
    setIsExpanded(true)
  }

  /**
   * What the collapsed Options line says. Anything set away from its default
   * has to appear here: a switch that is on but out of sight would make
   * Submit do something the screen doesn't admit to.
   */
  const optionSummary = [
    !hideProjectPicker ? projects.find(p => p.id === projectId)?.name : null,
    sequenceMode ? 'in order' : null,
  ].filter(Boolean).join(' · ')

  const handleVerify = () => {
    if (totalTasks === 0) return
    setReviewOpen(true)
  }

  const handleStage2Submit = async (resolved: BulkSection[]) => {
    setIsSubmitting(true)
    try {
      await commitSections(resolved)
      setRows([makeEmptyTaskRow()])
      onDone?.()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Cmd/Ctrl+Enter anywhere inside the card submits.
  const onCardKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
      e.preventDefault()
      void handleDirectSubmit()
    }
  }

  // ─── Collapsed state ────────────────────────────────────────────────────

  if (!isExpanded) {
    return (
      <div>
        {/* Phone: real input — one tap and you're typing. Enter adds via the
            same NLP pipeline; the sliders icon opens the full editor (with
            Verify for moving tasks between projects). */}
        <div className="flex h-11 items-center gap-2 rounded-lg border bg-card px-3 md:hidden">
          {justAdded ? (
            <Check className="h-4 w-4 shrink-0 text-green-500" />
          ) : (
            <Plus className="h-4 w-4 shrink-0 text-muted-foreground" />
          )}
          <input
            ref={quickInputRef}
            type="text"
            value={quickText}
            onChange={(e) => setQuickText(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                void handleQuickAdd()
              }
            }}
            placeholder={justAdded ? 'Added ✓ — keep going…' : 'Add a task…'}
            disabled={isSubmitting}
            className="min-w-0 flex-1 bg-transparent text-sm outline-none placeholder:text-muted-foreground/70"
          />
          {quickText.trim() && (
            <button
              onClick={() => void handleQuickAdd()}
              disabled={isSubmitting}
              className="shrink-0 rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground"
            >
              Add
            </button>
          )}
          <button
            onClick={expandWithSeed}
            className="shrink-0 p-1 text-muted-foreground"
            title="More options (projects, bulk entry, verify)"
            aria-label="Open full capture editor"
          >
            <SlidersHorizontal className="h-4 w-4" />
          </button>
        </div>
        {/* Desktop: unchanged collapsed button */}
        <Button
          variant="outline"
          className="hidden w-full justify-start text-muted-foreground gap-2 h-10 md:flex"
          onClick={() => setIsExpanded(true)}
        >
          <Plus className="h-4 w-4" />
          Quick capture...
        </Button>
      </div>
    )
  }

  // ─── Expanded state ─────────────────────────────────────────────────────

  return (
    <>
      <Card className="border-primary/50" onKeyDown={onCardKeyDown}>
        <CardContent className="p-3 space-y-2">
          {/* Header: what you're about to create, and a way out. The panel
              had grown a stack of eight controls around one text field —
              everything that isn't the rows, the warnings or Submit now
              lives one disclosure down. */}
          <div className="flex items-center justify-between gap-2">
            <span className="text-xs font-medium text-muted-foreground">
              {totalTasks === 0
                ? 'Add tasks'
                : `${totalTasks} task${totalTasks === 1 ? '' : 's'} · ${sections.length} section${sections.length === 1 ? '' : 's'}`}
            </span>
            <button
              type="button"
              onClick={() => { setIsExpanded(false); setRows([makeEmptyTaskRow()]) }}
              className="text-muted-foreground hover:text-foreground p-1 rounded hover:bg-muted"
              title="Close (Esc clears row)"
              aria-label="Close capture editor"
            >
              <X className="h-4 w-4" />
            </button>
          </div>

          {/* The row-based capture grid */}
          <BulkRowCapture rows={rows} onChange={setRows} projects={projects} />

          {/* Warnings never collapse. These change what Submit is about to
              do — a project that doesn't exist yet, a header that looks like
              a typo — and hiding them would make the button a gamble. */}
          {hasContent && (newProjectCount > 0 || fuzzyCount > 0) && (
            <div className="flex flex-wrap items-center gap-1.5 text-xs">
              {newProjectCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                  <FolderPlus className="h-3 w-3" /> {newProjectCount} new project{newProjectCount === 1 ? '' : 's'}
                </span>
              )}
              {fuzzyCount > 0 && (
                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  <Lightbulb className="h-3 w-3" /> {fuzzyCount} likely typo{fuzzyCount === 1 ? '' : 's'} (auto-accept on submit)
                </span>
              )}
            </div>
          )}

          {/* Options, collapsed. The summary on the line states anything set
              away from its default, so a setting can never be *on* and
              invisible at the same time — that would make Submit do
              something the screen doesn't admit to. */}
          <div className="rounded-md border border-dashed">
            <button
              type="button"
              onClick={() => setOptionsOpen((v) => !v)}
              aria-expanded={optionsOpen}
              className="flex w-full items-center gap-2 px-2.5 py-1.5 text-left text-xs text-muted-foreground hover:bg-accent/50"
            >
              {optionsOpen ? (
                <ChevronDown className="h-3.5 w-3.5 shrink-0" />
              ) : (
                <ChevronRight className="h-3.5 w-3.5 shrink-0" />
              )}
              <span className="font-medium">Options</span>
              {!optionsOpen && optionSummary && (
                <span className="truncate">— {optionSummary}</span>
              )}
            </button>

            {optionsOpen && (
              <div className="space-y-2 border-t px-2.5 py-2">
                {!hideProjectPicker ? (
                  <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                    <span>Default project</span>
                    <ProjectPicker
                      projects={projects}
                      value={projectId}
                      onChange={setProjectId}
                      placeholder="Project"
                      aria-label="Default project"
                      className="w-[220px] max-w-full"
                    />
                    <span className="hidden md:inline">(used for rows without a #Header)</span>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground">
                    Tasks land in this project. Use <code className="text-[11px]">#Other</code> to redirect.
                  </p>
                )}

                {/* "These go in this order" — one switch that writes the whole
                    chain, instead of opening each task to add the one before it. */}
                {onSequence && (
                  <button
                    type="button"
                    onClick={() => setSequenceMode((v) => !v)}
                    aria-pressed={sequenceMode}
                    disabled={totalTasks < 2}
                    className={cn(
                      'flex w-full items-center gap-2 rounded-md border px-2.5 py-2 text-left text-xs transition-colors',
                      sequenceMode
                        ? 'border-primary bg-primary/5'
                        : 'border-dashed text-muted-foreground hover:bg-accent',
                      totalTasks < 2 && 'opacity-50',
                    )}
                  >
                    <ListOrdered className={cn('h-4 w-4 shrink-0', sequenceMode && 'text-primary')} />
                    <span className="flex-1">
                      <span className={cn('font-medium', sequenceMode && 'text-foreground')}>
                        Do these in order
                      </span>
                      <span className="ml-1">
                        {totalTasks < 2
                          ? '— needs at least two tasks'
                          : sequenceMode
                            ? `— each of the ${totalTasks} waits for the one above it`
                            : '— link them so each waits for the one above'}
                      </span>
                    </span>
                    <span
                      className={cn(
                        'h-4 w-7 shrink-0 rounded-full transition-colors',
                        sequenceMode ? 'bg-primary' : 'bg-muted',
                      )}
                    >
                      <span
                        className={cn(
                          'block h-3 w-3 translate-y-0.5 rounded-full bg-background transition-transform',
                          sequenceMode ? 'translate-x-3.5' : 'translate-x-0.5',
                        )}
                      />
                    </span>
                  </button>
                )}

                <p className="hidden text-xs text-muted-foreground md:block">
                  Cmd/Ctrl+Enter submits · Esc clears the row · <code className="text-[11px]">#Project</code> on a line redirects it
                </p>
              </div>
            )}
          </div>

          <div className="flex items-center justify-end gap-2 pt-1">
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleVerify}
              disabled={isSubmitting || totalTasks === 0}
              title="Review and reorganise before creating"
            >
              Verify →
            </Button>
            <Button
              type="button"
              size="sm"
              onClick={handleDirectSubmit}
              disabled={isSubmitting || !projectId || totalTasks === 0}
              title="Cmd/Ctrl + Enter"
            >
              {isSubmitting ? (
                <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Creating…</>
              ) : (
                totalTasks === 0
                  ? 'Submit'
                  : totalTasks === 1
                    ? 'Submit task'
                    : `Submit ${totalTasks} task${totalTasks === 1 ? '' : 's'}${newProjectCount > 0 ? ` + ${newProjectCount} new` : ''}`
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Stage 2 review dialog — mount only when open so each open starts
          from a fresh parse of the rows. */}
      {reviewOpen && (
        <BulkAddReview
          open={reviewOpen}
          onOpenChange={setReviewOpen}
          initialSections={sections}
          projects={projects}
          defaultProjectId={projectId}
          onSubmit={handleStage2Submit}
        />
      )}
    </>
  )
}

export default QuickCapture
